import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useNotificationContext } from "../../contexts/notification-context";
import { storage } from "../../websocket/storage";
import { AnalysisProgressData } from "../../websocket/domains/analysis/analysis.types";
import { ErrorComponent } from "../error/error.component";
import { Analysis, AnalysisStatus } from "../../models/general/analysis";
import { translate } from "../../translate/translate";
import { NotificationStatusType, NotificationStyleConfig, NotificationType, StatusNavigationMap } from "../../types/notification.types";
import { ExportInfo, ExportStatus, ExportType } from "../../types/export.types";
import { ImportInfo, ImportStatus } from "../../types/import.types";
import { RfcType } from "../../types";
import { downloadExportFile } from "../../utils/download-utils";

const ANALYSIS_BORDER_COLORS: NotificationStyleConfig["borderColors"]["analysis"] = {
  [AnalysisStatus.PROCESSING]: "#2196F3",
  [AnalysisStatus.COMPLETED]: "#00D084",
  [AnalysisStatus.ERROR]: "#FF4C4C",
  [AnalysisStatus.CANCELED]: "#FFA500",
  [AnalysisStatus.WAITING]: "#5E1E9C",
  [AnalysisStatus.SAVE_ERROR]: "#FF9800",
};

const EXPORT_BORDER_COLORS: NotificationStyleConfig["borderColors"]["export"] = {
  [ExportStatus.WAITING]: "#007BFF",
  [ExportStatus.STARTED]: "#007BFF",
  [ExportStatus.FINISHED]: "#00D084",
  [ExportStatus.ERROR]: "#FF4C4C",
};

const EXPORT_STATUS_ALIASES: Record<string, ExportStatus> = {
  WAITING: ExportStatus.WAITING,
  STARTED: ExportStatus.STARTED,
  RUNNING: ExportStatus.STARTED,
  PROCESSING: ExportStatus.STARTED,
  FINISHED: ExportStatus.FINISHED,
  COMPLETED: ExportStatus.FINISHED,
  CONCLUDED: ExportStatus.FINISHED,
  DONE: ExportStatus.FINISHED,
  ERROR: ExportStatus.ERROR,
  FAILED: ExportStatus.ERROR,
};

const EXPORT_STATUS_NUMBER_ALIASES: Record<number, ExportStatus> = {
  1: ExportStatus.WAITING,
  2: ExportStatus.STARTED,
  3: ExportStatus.FINISHED,
  4: ExportStatus.ERROR,
};

const IMPORT_BORDER_COLORS: Record<ImportStatus, string> = {
  [ImportStatus.WAITING]: "#007BFF",
  [ImportStatus.STARTED]: "#2196F3",
  [ImportStatus.RUNNING]: "#2196F3",
  [ImportStatus.DONE]: "#00D084",
  [ImportStatus.FINISHED]: "#00D084",
  [ImportStatus.ERROR]: "#FF4C4C",
  [ImportStatus.FAILED]: "#FF4C4C",
  [ImportStatus.PENDING]: "#5E1E9C"
};

const ANALYSIS_BUTTON_LABELS: NotificationStyleConfig["buttonLabels"]["analysis"] = {
  [AnalysisStatus.PROCESSING]: translate.t("follow_execution"),
  [AnalysisStatus.COMPLETED]: translate.t("see_details"),
  [AnalysisStatus.ERROR]: translate.t("see_error"),
  [AnalysisStatus.CANCELED]: translate.t("see_details"),
  [AnalysisStatus.WAITING]: translate.t("see_queue"),
  [AnalysisStatus.SAVE_ERROR]: translate.t("see_error"),
};

const NAVIGATION_MAP: StatusNavigationMap = {
  [AnalysisStatus.COMPLETED]: {
    path: "/general/analysis/{id}/gds-calculation",
    options: { replace: true },
  },
  [AnalysisStatus.PROCESSING]: {
    path: "/general/analysis/{id}/processing",
  },
  [AnalysisStatus.WAITING]: {
    path: "/general/analysis",
  },
} as const;

export const useNotification = () => {
  const {
    notifications,
    badgeContent,
    markAsRead,
    markSingleAsRead,
    markAllAsRead,
    refreshNotifications,
  } = useNotificationContext();
  const navigate = useNavigate();

  const [anchorElNotification, setAnchorElNotification] = useState<HTMLElement | null>(null);

  const openNotificationMenu = Boolean(anchorElNotification);

  const hasExportTitle = (notification: NotificationType): boolean => {
    const text = `${notification.verb ?? ""} ${notification.description ?? ""}`.toLowerCase();
    return text.includes("export") || text.includes("exportação");
  };

  const isExportNotification = (
    notification: NotificationType,
  ): notification is NotificationType & { target: ExportInfo } => {
    const target = notification.target as ExportInfo | undefined;
    if (!target) return false;

    const hasExplicitType =
      target.type === ExportType.EXPORT_ANALYSIS ||
      target.type === ExportType.EXPORT_MATERIALS_DEMANDS ||
      target.type === ExportType.EXPORT_PO_COMPONENTS ||
      target.type === ExportType.EXPORT_STOCK;

    if (hasExplicitType) return true;

    return !!(hasExportTitle(notification) && target.file_url);
  };

  const normalizeExportStatus = (status: unknown): ExportStatus | undefined => {
    if (typeof status === "string") {
      return EXPORT_STATUS_ALIASES[status.toUpperCase()];
    }

    if (typeof status === "number") {
      return EXPORT_STATUS_NUMBER_ALIASES[status];
    }

    return undefined;
  };

  const isSapImportNotification = (notification: NotificationType): boolean => {
    const target = notification.target as any;
    const sapRfcTypes = [RfcType.BOM, RfcType.STOCK_AND_PO, RfcType.FORECAST];

    return target?.origin === "SAP" || sapRfcTypes.includes(target?.rfc_type);
  };

  const isImportNotification = (
    notification: NotificationType,
  ): notification is NotificationType & { target: ImportInfo & { type?: string } } => {
    const type = (notification.target as any)?.type;
    return type === "import_boms" ||
           type === "import_stock_and_po" ||
           type === "import_bom_progress" ||
           type === "import_stock_and_po_progress" ||
           type === "sap_import_status" ||
           isSapImportNotification(notification);
  };

  const getBorderColor = useCallback((notification: NotificationType): string => {
    if (isExportNotification(notification)) {
      const status = normalizeExportStatus(notification.target.status);
      const color = status ? EXPORT_BORDER_COLORS[status] : "#ccc";
      return `4px solid ${color}`;
    }

    if (hasExportTitle(notification)) {
      const status = normalizeExportStatus(notification.target?.status);
      const color = status ? EXPORT_BORDER_COLORS[status] : "#ccc";
      return `4px solid ${color}`;
    }

    if (isImportNotification(notification)) {
      return `4px solid ${IMPORT_BORDER_COLORS[notification.target.status as ImportStatus] ?? "#ccc"}`;
    }

    const status = notification?.target?.status as AnalysisStatus;
    const color = status ? (ANALYSIS_BORDER_COLORS[status] ?? "#ccc") : "#ccc";

    return `4px solid ${color}`;
  }, []);

  const getUserNameLabel = useCallback((notification: NotificationType): string => {
    return (notification as any)?.target?.user_name ?? "";
  }, []);

  const getNotificationTitle = useCallback((notification: NotificationType): string => {
    return notification.verb
      ? translate.t(notification.verb, { defaultValue: notification.verb })
      : "";
  }, []);

  const getNotificationDescription = useCallback((notification: NotificationType): string => {
    return notification.description
      ? translate.t(notification.description, { defaultValue: notification.description })
      : "";
  }, []);

  const shouldShowActionButton = useCallback(
    (notification: NotificationType): boolean => {
      if (isExportNotification(notification)) {
        return notification.target.status === ExportStatus.FINISHED;
      }
      if (hasExportTitle(notification)) {
        return false;
      }
      if (isSapImportNotification(notification)) {
        return false;
      }
      if (isImportNotification(notification)) {
        return true;
      }
      const status = notification.target?.status as AnalysisStatus;
      return status !== AnalysisStatus.CANCELED && status !== AnalysisStatus.ERROR;
    },
    [isExportNotification, isImportNotification],
  );

  const shouldShowRemainingTime = useCallback(
    (notification: NotificationType): boolean => {
      if (isExportNotification(notification)) {
        return false;
      }
      return notification.target?.status === AnalysisStatus.PROCESSING;
    },
    [isExportNotification],
  );

  const formatRemainingTime = useCallback(
    (notification: NotificationType): string => {
      const analysisId = notification.target?.id;
      if (!analysisId) return "...";
      const data = storage.get<AnalysisProgressData>(`analysis_${analysisId}`);
      return data?.remaining || "...";
    },
    [],
  );

  const handleOpenNotificationMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      markAllAsRead();
      setAnchorElNotification(event.currentTarget);
    },
    [markAllAsRead],
  );

  const handleCloseNotificationsMenu = useCallback((): void => {
    setAnchorElNotification(null);
  }, []);

  const navigateToAnalysis = useCallback(
    (status: NotificationStatusType, analysisId: number): void => {
      if (!analysisId) {
        return;
      }
      const navigationConfig = NAVIGATION_MAP[status as AnalysisStatus];

      if (!navigationConfig) {
        return;
      }

      let path = navigationConfig.path;
      if (path.includes("{id}")) {
        path = path.replace("{id}", analysisId.toString());
      }

      navigate(path, navigationConfig.options);
    },
    [navigate],
  );

  const handleClickNotification = useCallback(
    async (
      e: React.MouseEvent<HTMLButtonElement>,
      notification: NotificationType,
    ): Promise<void> => {
      e.stopPropagation();
      try {
        if (!notification.is_read) {
          await markSingleAsRead(notification.id);
        }

        if (isExportNotification(notification)) {
          handleDownloadExport(notification);
        } else if (isImportNotification(notification)) {
          const origin = notification.target?.origin;
          if (origin === "SAP") {
            navigate("/general/import-sap");
          } else if (origin === "PLANILHA") {
            navigate("/general/import-spreadsheets");
          } else {
            navigate("/general/import-data");
          }
          handleCloseNotificationsMenu();
        } else {
          handleAnalysisAction(notification);
        }
      } catch (error: any) {
        ErrorComponent(error);
      }
    },
    [markSingleAsRead, navigateToAnalysis, handleCloseNotificationsMenu, navigate],
  );

  const handleAnalysisAction = (notification: NotificationType): void => {
    if (notification.target) {
      const { status, id: analysisId } = notification.target as Analysis;
      if (status && analysisId) {
        navigateToAnalysis(status, analysisId);
        handleCloseNotificationsMenu();
      }
    }
  };

  const getExportNotificationTitle = useCallback((notification: any): string => {
    return notification.verb
      ? translate.t(notification.verb, { defaultValue: notification.verb })
      : translate.t("export_notification");
  }, []);

  const getButtonLabel = useCallback((notification: NotificationType): string => {
    if (isExportNotification(notification) || hasExportTitle(notification)) {
      const status = normalizeExportStatus(notification.target?.status);
      if (status === ExportStatus.FINISHED) {
        return translate.t("download");
      }
      return translate.t("see_details");
    }

    const status = notification.target?.status as AnalysisStatus | undefined;
    return status
      ? (ANALYSIS_BUTTON_LABELS[status] ?? translate.t("see_details"))
      : translate.t("see_details");
  }, []);

  const resolveDownloadType = (
    notification: NotificationType,
  ): "analysis" | "materials-demands" | "po-components" | "stock" => {
    const target = notification.target as ExportInfo;
    if (target.type) {
      switch (target.type) {
        case ExportType.EXPORT_ANALYSIS:
          return "analysis";
        case ExportType.EXPORT_PO_COMPONENTS:
          return "po-components";
        case ExportType.EXPORT_STOCK:
          return "stock";
        default:
          return "materials-demands";
      }
    }

    const text = `${notification.verb ?? ""} ${notification.description ?? ""}`.toLowerCase();
    if (text.includes("análise") || text.includes("analise")) {
      return "analysis";
    }
    if (text.includes("ordem de compra") || text.includes("po-components")) {
      return "po-components";
    }
    if (text.includes("estoque") || text.includes("stock")) {
      return "stock";
    }
    return "materials-demands";
  };

  const handleDownloadExport = useCallback(
    async (notification: NotificationType): Promise<void> => {
      if (!isExportNotification(notification)) return;

      const target = notification.target;
      const fileUrl = target.file_url;

      if (!fileUrl) {
        console.error("URL do arquivo não encontrada para download");
        return;
      }

      try {
        const downloadType = resolveDownloadType(notification);
        const result = await downloadExportFile(fileUrl, {}, downloadType);

        if (!result.success) {
          console.error("Erro no download:", result.error);
          alert("Erro ao baixar arquivo");
        }
      } catch (error) {
        console.error("Erro no download:", error);
        alert("Erro ao baixar arquivo");
      }
    },
    [],
  );

  const shouldShowUserName = useCallback((notification: NotificationType): boolean => {
    return !!(notification as any)?.target?.user_name;
  }, []);

  return {
    notifications,
    badgeContent,

    anchorElNotification,
    openNotificationMenu,

    getBorderColor,
    getButtonLabel,
    getNotificationTitle,
    getNotificationDescription,
    shouldShowActionButton,
    shouldShowRemainingTime,
    formatRemainingTime,
    isExportNotification,
    isImportNotification,

    markAsRead,
    markSingleAsRead,
    refreshNotifications,

    handleOpenNotificationMenu,
    handleCloseNotificationsMenu,
    handleClickNotification,

    getExportNotificationTitle,
    shouldShowUserName,
    handleDownloadExport,
    getUserNameLabel,
  };
};
